/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import cn from "classnames";

import { memo, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useAgentContext } from "../../contexts/AgentContext";
import LanguageSwitcher from "../language-switcher/LanguageSwitcher";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";

import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";

/**
 * Force audio output to use the loudspeaker (not earpiece) on mobile devices.
 * Deliberately avoids creating new AudioContexts — iOS limits concurrent contexts
 * to ~4-6 and creating/closing them rapidly destabilises recording and playback.
 */
const forceAudioToLoudspeaker = async (audioElement?: HTMLAudioElement): Promise<void> => {
  try {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Android Chrome: setSinkId('') routes audio to default output (loudspeaker)
    if (audioElement && 'setSinkId' in audioElement) {
      try {
        await (audioElement as any).setSinkId('');
      } catch (sinkError) {
        console.warn('⚠️ [Speaker] setSinkId failed:', sinkError);
      }
    }

    // iOS: audio routes to the loudspeaker by default in web apps — no extra work needed.
    // Do NOT create a new AudioContext here; the existing recording/playback contexts
    // already handle routing correctly once resumed via a user gesture.
  } catch (error) {
    console.warn('⚠️ [Speaker] Error forcing loudspeaker:', error);
  }
};


export type ControlTrayProps = {
  videoRef: RefObject<HTMLVideoElement>;
  children?: ReactNode;
  supportsVideo: boolean;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
};

type MediaStreamButtonProps = {
  isStreaming: boolean;
  onIcon: string;
  offIcon: string;
  start: () => Promise<any>;
  stop: () => any;
};

/**
 * button used for triggering webcam or screen-capture
 */
const MediaStreamButton = memo(
  ({ isStreaming, onIcon, offIcon, start, stop }: MediaStreamButtonProps) =>
    isStreaming ? (
      <button className="action-button" onClick={stop}>
        <span className="material-symbols-outlined">{onIcon}</span>
      </button>
    ) : (
      <button className="action-button" onClick={start}>
        <span className="material-symbols-outlined">{offIcon}</span>
      </button>
    )
);

// Constants for audio optimization
const VAD_THRESHOLD = 0.005; // Volume threshold for Voice Activity Detection (lowered for sensitivity)
const VAD_HOLD_MS = 300; // Hold speech state for 300ms after volume drops (prevents choppy cutoffs)
const IDLE_TIMEOUT_MS = 300000; // 5 minutes idle timeout (session stays open between flows)

function ControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => {},
  supportsVideo,
}: ControlTrayProps) {
  const webcam = useWebcam();
  const videoStreams = [webcam];
  const [activeVideoStream, setActiveVideoStream] =
    useState<MediaStream | null>(null);
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const [recorderReady, setRecorderReady] = useState(false); // Track when micGainNode is ready
  const [, setIsSpeaking] = useState(false); // VAD: track if user is speaking (value read via isSpeakingRef)
  const renderCanvasRef = useRef<HTMLCanvasElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null); // Idle connection timer
  const lastActivityRef = useRef<number>(Date.now()); // Track last activity time
  
  // Refs for values that need to be current in audio callbacks (avoid stale closures)
  const mutedRef = useRef(muted);
  const isSpeakingRef = useRef(false);
  const speechHoldTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Keep mutedRef in sync with muted state
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  // Use agent context instead of live API context
  const { 
    connected, 
    disconnect, 
    volume, 
    audioStreamer, 
    sendRealtimeInput,
    connect,
    isConnecting,
    send,
    transitionTo,
    currentAgentId,
  } = useAgentContext();

  // "Talk to someone" shortcut — transitions directly to human_handoff_agent
  const handleTalkToSomeone = () => {
    resetIdleTimer();
    if (!connected) return;
    // If we're already in handoff, just send the phrase
    if (currentAgentId === 'human_handoff_agent') {
      send('I want to talk to someone', true);
      return;
    }
    transitionTo('human_handoff_agent', 'User requested human contact via button', {});
  };

  // Reset idle timer on any activity
  const resetIdleTimer = () => {
    lastActivityRef.current = Date.now();
    
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    if (connected) {
      idleTimerRef.current = setTimeout(() => {
        console.log('⏰ [Idle] No activity for 5 minutes, disconnecting...');
        disconnect();
      }, IDLE_TIMEOUT_MS);
    }
  };

  // Idle timeout management — depends only on `connected`.
  // `disconnect` is intentionally excluded: it is used inside a setTimeout closure
  // and AgentContext guarantees it is stable (no identity change on re-renders).
  useEffect(() => {
    if (connected) {
      resetIdleTimer();
      console.log('⏰ [Idle] Started 5-minute idle timer');
    } else {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      setRecorderReady(false);
      setIsSpeaking(false);
    }
    
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Reset idle timer on every agent transition so flows don't time out mid-session
  useEffect(() => {
    if (connected && currentAgentId) resetIdleTimer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAgentId]);

  // Force audio to loudspeaker on mobile devices
  useEffect(() => {
    if (connected && audioStreamer) {
      // Call loudspeaker enforcement when connected
      forceAudioToLoudspeaker();
      
      // Also force it whenever the audio context state changes
      const audioContext = audioStreamer.context;
      if (audioContext) {
        const handleStateChange = () => {
          console.log('🔊 [Speaker] AudioContext state changed to:', audioContext.state);
          if (audioContext.state === 'running') {
            forceAudioToLoudspeaker();
          }
        };
        
        audioContext.addEventListener('statechange', handleStateChange);
        
        return () => {
          audioContext.removeEventListener('statechange', handleStateChange);
        };
      }
    }
  }, [connected, audioStreamer]);

  // MOBILE FIX: Resume AudioContexts when the page becomes visible again.
  // On mobile, switching apps or locking the screen suspends all AudioContexts.
  // Without this, audio playback and microphone capture silently stop until reload.
  useEffect(() => {
    if (!connected) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      console.log('👁️ [Mobile Fix] Page visible – resuming AudioContexts');

      // Resume output context
      if (audioStreamer?.context?.state === 'suspended') {
        try {
          await audioStreamer.context.resume();
          if (audioStreamer.gainNode) {
            audioStreamer.gainNode.gain.setValueAtTime(1.0, audioStreamer.context.currentTime);
          }
        } catch (e) { console.warn('⚠️ [Visibility] Output context resume failed:', e); }
      }

      // Resume input (recorder) context
      if (audioRecorder.audioContext?.state === 'suspended') {
        try {
          await audioRecorder.audioContext.resume();
        } catch (e) { console.warn('⚠️ [Visibility] Input context resume failed:', e); }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [connected, audioStreamer, audioRecorder]);

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);

  // Update activeVideoStream when webcam stream changes (e.g., after camera switching)
  useEffect(() => {
    if (webcam.isStreaming && webcam.stream) {
      console.log('📹 Updating video element with new webcam stream after camera switch');
      setActiveVideoStream(webcam.stream);
      onVideoStreamChange(webcam.stream);
    } else if (!webcam.isStreaming) {
      console.log('📹 Clearing video element - webcam stopped');
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }
  }, [webcam.stream, webcam.isStreaming, onVideoStreamChange]);
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`
    );
  }, [inVolume]);

  // Handle audio recording - start once when connected
  // Mute uses GainNode (makes audio silent), idle timeout auto-disconnects
  useEffect(() => {
    const onData = (base64: string) => {
      // Only check mute - GainNode already handles making audio silent when muted
      // Removed VAD filtering as it caused latency issues due to timing mismatch
      if (mutedRef.current) {
        // Don't send anything when muted - save tokens
        return;
      }
      
      // Reset idle timer on any audio activity
      resetIdleTimer();
      
      sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };
    
    const onVolume = (vol: number) => {
      setInVolume(vol);
      
      // Track speaking state for UI feedback (visual indicator)
      // Not used for filtering audio anymore
      const speaking = vol > VAD_THRESHOLD;
      
      if (speaking) {
        if (speechHoldTimerRef.current) {
          clearTimeout(speechHoldTimerRef.current);
          speechHoldTimerRef.current = null;
        }
        isSpeakingRef.current = true;
        setIsSpeaking(true);
        resetIdleTimer();
      } else if (isSpeakingRef.current) {
        // Use hold timer for smooth UI transition
        if (!speechHoldTimerRef.current) {
          speechHoldTimerRef.current = setTimeout(() => {
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            speechHoldTimerRef.current = null;
          }, VAD_HOLD_MS);
        }
      }
    };
    
    // Start the recorder once when connected
    if (connected && audioRecorder) {
      audioRecorder.on("data", onData).on("volume", onVolume);
      
      // Await start() to ensure micGainNode is created before applying mute
      audioRecorder.start().then(() => {
        console.log('🎤 [AudioRecorder] Started and ready');
        setRecorderReady(true);
        
        // Apply pending mute state now that micGainNode exists
        if (mutedRef.current) {
          audioRecorder.muteMic();
          console.log('🔇 [Mute] Applied pending mute state after recorder ready');
        }
      }).catch(err => {
        console.error('🎤 [AudioRecorder] Failed to start:', err);
      });
      
      // MOBILE FIX: Ensure audio output context stays active
      if (audioStreamer) {
        const audioContext = audioStreamer.context;
        if (audioContext) {
          audioContext.resume().then(() => {
            console.log('🔊 [Mobile Fix] Audio output context resumed');
            if (audioStreamer.gainNode) {
              audioStreamer.gainNode.gain.cancelScheduledValues(audioContext.currentTime);
              audioStreamer.gainNode.gain.setValueAtTime(1.0, audioContext.currentTime);
            }
          }).catch(err => {
            console.warn('⚠️ [Mobile Fix] Failed to resume output context:', err);
          });
        }
      }
    } else if (!connected && audioRecorder) {
      // Only stop when disconnecting completely
      audioRecorder.stop();
      setRecorderReady(false);
      isSpeakingRef.current = false;
      
      // Clear speech hold timer on disconnect
      if (speechHoldTimerRef.current) {
        clearTimeout(speechHoldTimerRef.current);
        speechHoldTimerRef.current = null;
      }
    }
    
    return () => {
      audioRecorder.off("data", onData).off("volume", onVolume);
      // Clear speech hold timer on cleanup
      if (speechHoldTimerRef.current) {
        clearTimeout(speechHoldTimerRef.current);
        speechHoldTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, sendRealtimeInput, audioRecorder, audioStreamer]);

  // Separate useEffect for applying mute state changes after recorder is ready
  useEffect(() => {
    if (connected && recorderReady && audioRecorder && audioRecorder.micGainNode) {
      // Apply mute state via gain (no stream interruption)
      if (muted) {
        audioRecorder.muteMic();
        console.log('🔇 [Effect] Microphone muted via gain');
      } else {
        audioRecorder.unmuteMic();
        console.log('🔊 [Effect] Microphone unmuted via gain');
      }
    }
  }, [muted, connected, recorderReady, audioRecorder]); // Added recorderReady to ensure micGainNode exists

  useEffect(() => {
    if (videoRef.current) {
      console.log('🎥 Setting video srcObject:', activeVideoStream ? 'New stream' : 'null');
      videoRef.current.srcObject = activeVideoStream;
      
      // Force video to load and play the new stream (important for Android)
      if (activeVideoStream) {
        videoRef.current.load();
        videoRef.current.play().catch((error) => {
          console.warn('📹 Video play failed (this is usually normal):', error);
        });
      }
    }

    let timeoutId = -1;

    function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d")!;
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;
      if (canvas.width + canvas.height > 0) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 1.0);
        const data = base64.slice(base64.indexOf(",") + 1, Infinity);
        sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
      }
      if (connected) {
        timeoutId = window.setTimeout(sendVideoFrame, 1000 / 0.5);
      }
    }
    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, sendRealtimeInput, videoRef]);

  //handler for swapping from one video-stream to the next
  const changeStreams = (next?: UseMediaStreamResult) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
  };

  // Handle mute/unmute with gain-based control (no stream interruption)
  const handleMuteToggle = async () => {
    resetIdleTimer();

    const newMutedState = !muted;

    // CRITICAL MOBILE FIX: Update the ref SYNCHRONOUSLY before setMuted() so that any
    // audio chunks already queued in the AudioWorklet pipeline are suppressed immediately.
    // Without this, React defers the effect that syncs mutedRef until after the next
    // render paint, leaving a window where onData() still sends audio to Gemini.
    mutedRef.current = newMutedState;
    setMuted(newMutedState);

    // Apply mute via GainNode – keeping the stream alive avoids mobile audio re-routing
    if (recorderReady && audioRecorder.micGainNode) {
      // MOBILE FIX: Resume the INPUT AudioContext if iOS suspended it after the last
      // user interaction. setValueAtTime() on a suspended context has no effect.
      if (audioRecorder.audioContext?.state !== 'running') {
        try {
          await audioRecorder.audioContext?.resume();
        } catch (e) {
          console.warn('⚠️ [Mute] Failed to resume recorder AudioContext:', e);
        }
      }
      if (newMutedState) {
        audioRecorder.muteMic();
        console.log('🔇 [Mute] Microphone muted via gain');
      } else {
        audioRecorder.unmuteMic();
        console.log('🔊 [Unmute] Microphone unmuted via gain');
      }
    } else {
      console.log('⏳ [Mute] Recorder not ready yet, mute will be applied when ready');
    }

    // MOBILE FIX: Keep output routed to loudspeaker on every toggle
    await forceAudioToLoudspeaker();

    // MOBILE FIX: Resume OUTPUT AudioContext and lock gain to 1.0
    if (audioStreamer) {
      try {
        const audioContext = audioStreamer.context;
        if (audioContext) {
          if (audioContext.state !== 'running') {
            await audioContext.resume();
          }
          if (audioStreamer.gainNode) {
            audioStreamer.gainNode.gain.cancelScheduledValues(audioContext.currentTime);
            audioStreamer.gainNode.gain.setValueAtTime(1.0, audioContext.currentTime);
          }
        }
      } catch (error) {
        console.warn('⚠️ [Mobile Fix] Error in handleMuteToggle:', error);
      }
    }
  };

  return (
    <section className="control-tray">
      <canvas style={{ display: "none" }} ref={renderCanvasRef} />
      <nav className={cn("actions-nav", { disabled: !connected, hidden: !connected })}>
        <button
          className={cn("action-button mic-button")}
          onClick={handleMuteToggle}
        >
          {!muted ? (
            <span className="material-symbols-outlined filled">mic</span>
          ) : (
            <span className="material-symbols-outlined filled">mic_off</span>
          )}
        </button>

        {supportsVideo && (
          <>
            <MediaStreamButton
              isStreaming={webcam.isStreaming}
              start={changeStreams(webcam)}
              stop={changeStreams()}
              onIcon="videocam_off"
              offIcon="videocam"
            />
            {/* Camera switch button - only show when webcam is active and multiple cameras available */}
            {webcam.isStreaming && webcam.canSwitchCamera && (
              <button
                className={cn("action-button camera-switch", { 
                  switching: webcam.isSwitching 
                })}
                onClick={webcam.switchCamera}
                disabled={webcam.isSwitching}
                title={webcam.isSwitching 
                  ? 'Switching camera...' 
                  : `Switch to ${webcam.facingMode === 'user' ? 'back' : 'front'} camera`
                }
              >
                {webcam.isSwitching ? (
                  <span className="material-symbols-outlined rotating">
                    sync
                  </span>
                ) : (
                  <span className="material-symbols-outlined">
                    {webcam.facingMode === 'user' ? 'camera_rear' : 'camera_front'}
                  </span>
                )}
              </button>
            )}
          </>
        )}
        
        <div className="action-button no-action outlined">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>

        {children}
      </nav>

      {/* Language switcher — always visible so user can change before connecting */}
      <LanguageSwitcher />

      {/* Talk to Someone shortcut — shown when connected */}
      {connected && (
        <button
          className="talk-to-someone-btn"
          onClick={handleTalkToSomeone}
          title="Request to speak with a human operator"
        >
          <span className="material-symbols-outlined">support_agent</span>
          <span className="talk-to-someone-label">Talk to Someone</span>
        </button>
      )}

      <div className={cn("connection-container", { connected })}>
        <div className="connection-button-container">
          <button
            ref={connectButtonRef}
            className={cn("action-button connect-toggle", { 
              connected,
              connecting: isConnecting
            })}
            onClick={() => {
              resetIdleTimer(); // Reset on user interaction
              if (connected) {
                disconnect();
              } else if (!isConnecting) {
                connect();
              }
            }}
            disabled={isConnecting}
          >
            {connected ? (
            <img src="/animationPause.gif" alt="" style={{width: '90px', height: '90px',zIndex: 1}} />
          ) : isConnecting ? (
            <span className="material-symbols-outlined filled rotating">sync</span>
          ) : (
            <span className="material-symbols-outlined filled">play_arrow</span>
          )}
            {/* <span className="material-symbols-outlined filled">
              {connected ? "pause" : "play_arrow"}
            </span> */}
          </button>
        </div>
        <span className="text-indicator">
          {isConnecting ? 'Connecting...' : 'Streaming'}
        </span>
      </div>
    </section>
  );
}

export default memo(ControlTray);
